import React, { useState, useEffect, useRef } from 'react';

const TOAST_DURATION = 25;

const NewOrderToast = ({ order, onAccept, onDecline, onDismiss }) => {
  const [timeLeft, setTimeLeft] = useState(TOAST_DURATION);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // Animate in
    const showTimer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(showTimer);
  }, []);

  useEffect(() => {
    if (timeLeft <= 0) {
      handleDecline();
      return;
    }
    timerRef.current = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearTimeout(timerRef?.current);
  }, [timeLeft]);

  const handleAccept = () => {
    clearTimeout(timerRef?.current);
    onAccept?.(order);
  };

  const handleDecline = () => {
    clearTimeout(timerRef?.current);
    setVisible(false);
    setTimeout(() => onDecline?.(order?.id), 300);
  };

  const progress = (timeLeft / TOAST_DURATION) * 100;
  const isUrgent = timeLeft <= 8;

  return (
    <div
      className={`transition-all duration-300 ease-out ${
        visible ? 'translate-y-0 opacity-100 scale-100' : '-translate-y-4 opacity-0 scale-95'
      }`}
    >
      <div className={`w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden border ${
        order?.priority === 'high' ?'bg-[#0a1628] border-[#ffd700]/50' :'bg-[#0f2040] border-white/15'
      }`}>
        {/* Timer progress bar */}
        <div className="relative h-1.5 bg-white/10">
          <div
            className={`h-full transition-all duration-1000 ease-linear ${isUrgent ? 'bg-red-500' : 'bg-[#ffd700]'}`}
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {/* Pulsing ring for new order */}
              <div className="relative flex-shrink-0">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#ffd700]/20">
                  {order?.type === 'ride' ? (
                    <svg viewBox="0 0 24 24" fill="#ffd700" className="w-5 h-5">
                      <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="#ffd700" className="w-5 h-5">
                      <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                    </svg>
                  )}
                </div>
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ffd700] opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-[#ffd700]" />
                </span>
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="text-white font-black text-sm">New {order?.type === 'ride' ? 'Ride' : 'Delivery'} Request!</p>
                  {order?.priority === 'high' && (
                    <span className="text-[10px] font-bold text-[#ffd700] bg-[#ffd700]/15 px-1.5 py-0.5 rounded-full">PRIORITY</span>
                  )}
                </div>
                <p className="text-white/40 text-xs">{order?.id}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 justify-end">
                <span className="text-[#ffd700] font-black text-xl">KSh {order?.fare}</span>
                {order?.surgeMultiplier && (
                  <span className="text-[10px] font-bold text-orange-400 bg-orange-400/10 px-1.5 py-0.5 rounded-full">{order?.surgeMultiplier}x</span>
                )}
              </div>
              <span className="text-white/40 text-xs">{order?.distance}</span>
            </div>
          </div>

          {/* Route */}
          <div className="bg-white/5 rounded-xl p-3 mb-3 space-y-2">
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
              </div>
              <div className="min-w-0">
                <p className="text-white/40 text-[10px] uppercase tracking-wider">Pickup</p>
                <p className="text-white text-sm font-medium leading-tight truncate">{order?.pickup}</p>
              </div>
            </div>
            <div className="ml-2.5 w-px h-3 bg-white/10" />
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-[#ffd700]/20 border border-[#ffd700]/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#ffd700]" />
              </div>
              <div className="min-w-0">
                <p className="text-white/40 text-[10px] uppercase tracking-wider">Drop-off</p>
                <p className="text-white text-sm font-medium leading-tight truncate">{order?.dropoff}</p>
              </div>
            </div>
          </div>

          {/* Passenger + ETA row */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#ffd700]/20 flex items-center justify-center">
                <span className="text-[11px] font-bold text-[#ffd700]">{order?.passengerName?.[0]}</span>
              </div>
              <span className="text-white/70 text-xs font-medium">{order?.passengerName}</span>
              <div className="flex items-center gap-0.5">
                <svg viewBox="0 0 24 24" fill="#ffd700" className="w-3 h-3">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                </svg>
                <span className="text-white/50 text-xs">{order?.passengerRating}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 bg-white/5 rounded-full px-2.5 py-1">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              <span className="text-white/60 text-xs">{order?.eta}</span>
            </div>
          </div>

          {/* Accept / Decline buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleDecline}
              className="btn-secondary flex-1 hover:border-red-500/40 hover:text-red-500 hover:bg-red-50"
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              className={`flex-[2] ${isUrgent ? 'btn-danger' : 'btn-primary'}`}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              Accept · {timeLeft}s
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewOrderToast;
